import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { isDefined, isNil } from '@trimble-gcs/common';
import { SortInfo } from '../scandata-query.models';
import { ScandataModel } from '../scandata.models';
import {
  ClearRecycledScans,
  RemoveRecycledScans,
  SetPreviewVisible,
  SetRecycledScans,
  SetSelectedRecycledScans,
  SetSortInfo,
} from './scandata-recycle.actions';

export interface ScandataRecycleStateModel {
  recycledScans: ScandataModel[];
  sortInfo: SortInfo;
  previewVisible: boolean;
}

const defaultState: ScandataRecycleStateModel = {
  recycledScans: [],
  sortInfo: { sortBy: 'deletedDate', sortDirection: 'desc' },
  previewVisible: true,
};

@State<ScandataRecycleStateModel>({
  name: 'scandataRecycleState',
  defaults: defaultState,
})
@Injectable()
export class ScandataRecycleState {
  @Selector() static recycledScans(state: ScandataRecycleStateModel): ScandataModel[] {
    return state.recycledScans;
  }

  @Selector() static sortInfo(state: ScandataRecycleStateModel): SortInfo {
    return state.sortInfo;
  }

  @Selector() static previewVisible(state: ScandataRecycleStateModel) {
    return state.previewVisible;
  }

  @Selector() static chronologicalSelected(state: ScandataRecycleStateModel): ScandataModel[] {
    return [...state.recycledScans.filter((model) => model.selected)].sort(
      (a, b) => (a.selectedIndex ?? 0) - (b.selectedIndex ?? 0),
    );
  }

  @Action(SetRecycledScans) setRecycledScans(
    ctx: StateContext<ScandataRecycleStateModel>,
    { scans }: SetRecycledScans,
  ) {
    ctx.setState(patch<ScandataRecycleStateModel>({ recycledScans: scans }));
  }

  @Action(SetSortInfo) setSortInfo(
    ctx: StateContext<ScandataRecycleStateModel>,
    { sortInfo }: SetSortInfo,
  ) {
    ctx.setState(patch<ScandataRecycleStateModel>({ sortInfo }));
  }

  @Action(SetPreviewVisible) setPreviewVisible(
    ctx: StateContext<ScandataRecycleStateModel>,
    action: SetPreviewVisible,
  ) {
    ctx.patchState({ previewVisible: action.previewVisible });
  }

  @Action(SetSelectedRecycledScans) setSelectedRecycledScans(
    ctx: StateContext<ScandataRecycleStateModel>,
    { scans }: SetSelectedRecycledScans,
  ) {
    const data = ctx.getState().recycledScans.slice();
    const nextAvailableIndex = getLastSelectedIndex(data) + 1;

    data.forEach((model) => {
      const updateScan = scans.find((s) => s.id === model.id);
      if (isNil(updateScan)) return;

      const selected = updateScan.selected;

      model.selected = selected;
      model.selectedIndex = selected
        ? scans.filter((scan) => scan.selected).findIndex((s) => s.id === model.id) +
          nextAvailableIndex
        : undefined;
    });

    ctx.patchState({ recycledScans: data });
  }

  @Action(RemoveRecycledScans) removeRecycledScans(
    ctx: StateContext<ScandataRecycleStateModel>,
    { scanIds }: RemoveRecycledScans,
  ) {
    const scans = ctx.getState().recycledScans.filter((scan) => !scanIds.includes(scan.id));
    ctx.patchState({ recycledScans: scans });
  }

  @Action(ClearRecycledScans) clearRecycledScans(ctx: StateContext<ScandataRecycleStateModel>) {
    ctx.patchState({ recycledScans: [] });
  }
}

function getLastSelectedIndex(data: ScandataModel[]) {
  return (
    data
      .map((scan) => scan.selectedIndex)
      .filter(isDefined)
      .toSorted((a, b) => a - b)
      .at(-1) ?? -1
  );
}
